import { EyeOff } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { CustomInput } from "@/components/forms/customInput"
import { EyeIcon } from "@/components/icons"
import { AuthWrapper } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/features/auth/store/authStore"
import { SocialLoginButtons } from "../components/SocialLoginButtons"
import { useAuthForm } from "../hooks/useAuthForm"
import { type LoginFormData, loginSchema } from "../schemas/auth.schema"

export function LoginScreen() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const login = useAuthStore(state => state.login)
  const [showPassword, setShowPassword] = useState(false)

  const { form, handleSubmit, isSubmitting, serverError, clearServerError } = useAuthForm(
    loginSchema,
    async (data: LoginFormData) => {
      const response = await login(data.email, data.password)

      // If email not verified, redirect to verify email page
      if (!response.user.emailVerified) {
        navigate("/verify-email", { state: { email: data.email } })
        return
      }

      // Smart routing based on user completion state
      if (!response.user.onboardingCompleted) {
        navigate("/onboarding/permissions")
      } else {
        navigate("/")
      }
    },
  )

  const emailRegister = form.register("email")
  const passwordRegister = form.register("password")

  return (
    <AuthWrapper className="my-28">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6 px-5 sm:px-10 md:px-27">
          <div className="text-center mb-10 mt-5">
            <h2 className="text-2xl font-[500] text-primary font-base font-semibold">
              Login to Rituality
            </h2>
          </div>

          <div className="space-y-2">
            <div className="space-y-5">
              <CustomInput
                label={t("login.emailLabel")}
                type="email"
                placeholder={t("login.emailPlaceholder")}
                required={false}
                {...emailRegister}
                error={form.formState.errors.email?.message as string | undefined}
                disabled={isSubmitting}
                onChange={e => {
                  emailRegister.onChange?.(e)
                  clearServerError()
                }}
                className="pb-1"
              />

              <div className="relative">
                <CustomInput
                  label={t("login.passwordLabel")}
                  type={showPassword ? "text" : "password"}
                  required={false}
                  placeholder={t("login.passwordPlaceholder")}
                  {...passwordRegister}
                  error={
                    (form.formState.errors.password?.message || serverError) as string | undefined
                  }
                  disabled={isSubmitting}
                  onChange={e => {
                    passwordRegister.onChange?.(e)
                    clearServerError()
                  }}
                  className="pb-1 pr-8"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center text-body cursor-pointer"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff size={17} className="text-primary" />
                  ) : (
                    <EyeIcon size={17} />
                  )}
                </button>
              </div>
            </div>

            <div className="text-center w-full pb-0 cursor-pointer flex items-end justify-end mt-0">
              <button
                type="button"
                className="text-[13px] text-body bg-transparent border-none p-0 me-1 leading-[25px]"
                onClick={() => navigate("/forgot-password")}
              >
                Trouble signing in? <span className="font-semibold">Reset password</span>
              </button>
            </div>

            <div className="py-2 mt-3.5">
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="uppercase"
                isLoading={isSubmitting}
                fullWidth
              >
                Login
              </Button>
            </div>

            <SocialLoginButtons isLoading={isSubmitting} />
          </div>
        </div>

        <div className="text-center pt-12 w-full space-y-4.5">
          <p className="font-semibold text-[14px]">Don't have an account?</p>
          <div className="mx-auto sm:px-0 px-5">
            <Button
              type="submit"
              variant="outline"
              size="md"
              className="uppercase border-white w-full sm:w-[365px]"
              onClick={() => navigate("/signup")}
            >
              Sign Up
            </Button>
          </div>
          <p className="text-[14px] mt-8 ms-2 text-body leading-[25px]">
            {" "}
            By continuing, you agree to our{" "}
            <a
              href="https://myrituality.com/terms/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold"
            >
              Terms & Conditions
            </a>{" "}
            and{" "}
            <a
              href="https://myrituality.com/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </form>
    </AuthWrapper>
  )
}
